import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { BinderGrid, type CardRow } from "@/routes/binder";
import { BINDER_CAPACITY, BINDER_PAGES } from "@/lib/binder-shared";
import { getSharedBinder } from "@/lib/binder.functions";
import { useSession } from "@/lib/user-data";

export const Route = createFileRoute("/shared-binder/$userId")({
  head: ({ params }) => ({
    meta: [
      { title: `Shared Palworld Card Binder (collector ${params.userId.slice(0, 8)}) — Pal Stars` },
      { name: "description", content: `Pal Stars collector ${params.userId.slice(0, 8)} shared their completed Palworld card binder. Sign in with a free account to flip through all 108 pockets.` },
      { property: "og:title", content: `Shared Palworld card binder (collector ${params.userId.slice(0, 8)}) — Pal Stars` },
      { property: "og:description", content: `108 Palworld cards from collector ${params.userId.slice(0, 8)}, filed pocket by pocket on Pal Stars.` },
      { property: "og:url", content: `https://palstars.com/shared-binder/${params.userId}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: `https://palstars.com/shared-binder/${params.userId}` }],
  }),
  component: SharedBinderPage,
});

function SharedBinderPage() {
  const { userId } = Route.useParams();
  const { session, loading } = useSession();
  const fetchBinder = useServerFn(getSharedBinder);
  const [state, setState] = useState<{ owner: string; cards: CardRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!session) return;
    fetchBinder({ data: { user_id: userId } })
      .then((r) => setState(r as unknown as { owner: string; cards: CardRow[] }))
      .catch((e) => setError(e instanceof Error ? e.message : "That binder could not be loaded."));
  }, [session, userId, fetchBinder]);

  const map = useMemo(() => {
    const m = new Map<number, CardRow>();
    (state?.cards ?? []).forEach((c) => { if (c.binder_slot !== null) m.set(c.binder_slot, c); });
    return m;
  }, [state]);

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-16 text-muted-foreground">Loading binder…</div>;

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-14 text-center">
        <h1 className="text-3xl font-bold">This binder is shared with members</h1>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          Shared Palworld binders on Pal Stars open for account holders only. A free account also gives you 5 booster
          packs every day and your own {BINDER_CAPACITY}-pocket binder. Single cards can still be shared publicly.
        </p>
        <Link to="/auth" className="mt-5 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Create a free account
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Shared collector binder</p>
        <h1 className="mt-2 text-3xl font-bold">{state?.owner ?? "Trainer"}'s Palworld card binder</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {map.size} of {BINDER_CAPACITY} pockets filled · page {page + 1} of {BINDER_PAGES}
        </p>
      </header>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="glass rounded-2xl p-4 sm:p-6">
        <BinderGrid page={page} cards={map} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40">
          ← Previous page
        </button>
        <button type="button" onClick={() => setPage((p) => Math.min(BINDER_PAGES - 1, p + 1))} disabled={page >= BINDER_PAGES - 1} className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40">
          Next page →
        </button>
        <Link to="/binder" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Open my binder
        </Link>
      </div>
    </div>
  );
}
